import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { invoiceDetailKey } from '../api/use-invoice-detail';
import { DeletePaymentDialog } from '../dialogs/delete-payment-dialog';
import { EditTransferReferenceDialog } from '../dialogs/edit-transfer-reference-dialog';
import { formatAmount } from '../lib/format-amount';
import { PAYMENT_FIELDS } from '../lib/invoice-detail-fields';
import { computeOutstanding, sumPaid } from '../lib/payment-totals';
import { isRaiseMarker } from '../lib/raise-marker';
import { AddPaymentControl } from './add-payment-button';
import { canRecordPayments } from './inline-payments-list';

import type { InvoiceRow, PaymentRow } from '../types';

export function PaymentsSection({
  invoice,
  payments,
}: Readonly<{ invoice: InvoiceRow; payments: PaymentRow[] }>) {
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<PaymentRow | null>(null);
  const eligible = canRecordPayments(invoice);

  const invalidateDetail = () => {
    void queryClient.invalidateQueries({ queryKey: invoiceDetailKey(invoice.id) });
  };

  return (
    <section aria-labelledby="payments-heading" className="space-y-2">
      <PaymentsHeader invoice={invoice} payments={payments} />
      {payments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No payments recorded.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {payments.map((p) => (
            <PaymentRowItem
              key={p.id}
              invoice={invoice}
              payment={p}
              // The raise marker reverses via Unmark, not delete; tranche rows stay deletable.
              canDelete={eligible && p.deleted_at === null && !isRaiseMarker(p)}
              onChanged={invalidateDetail}
              onDelete={setDeleteTarget}
            />
          ))}
        </ul>
      )}
      <PaymentsFooter invoice={invoice} eligible={eligible} onChanged={invalidateDetail} />
      {deleteTarget !== null ? (
        <DeletePaymentDialog
          open={true}
          onOpenChange={(next) => {
            if (!next) setDeleteTarget(null);
          }}
          invoice={invoice}
          payment={deleteTarget}
          onDeleted={() => {
            setDeleteTarget(null);
            invalidateDetail();
          }}
        />
      ) : null}
    </section>
  );
}

function PaymentsHeader({
  invoice,
  payments,
}: Readonly<{ invoice: InvoiceRow; payments: PaymentRow[] }>) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h3 id="payments-heading" className="text-sm font-semibold">
        PAYMENTS
      </h3>
      <PaymentsTotals invoice={invoice} payments={payments} />
    </div>
  );
}

// The Settlement group's single verb: Add payment covers manual entry and
// matching a cash-ledger event. While a cash event is linkable the button
// offers the source choice; once the invoice is fully Paid only manual entry
// remains and the button opens it directly.
function PaymentsFooter({
  invoice,
  eligible,
  onChanged,
}: Readonly<{ invoice: InvoiceRow; eligible: boolean; onChanged: () => void }>) {
  if (!eligible) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <AddPaymentControl invoice={invoice} onChanged={onChanged} />
    </div>
  );
}

function PaymentsTotals({
  invoice,
  payments,
}: Readonly<{ invoice: InvoiceRow; payments: PaymentRow[] }>) {
  const paid = formatAmount(sumPaid(payments).toFixed(2), invoice.currency);
  const outstanding = formatAmount(
    computeOutstanding(invoice, payments).toFixed(2),
    invoice.currency,
  );
  // Launchpad parity: inline beside the PAYMENTS heading — Paid in green,
  // Outstanding in orange.
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium">
      <span className="text-emerald-600 dark:text-emerald-400" data-testid="payments-total-paid">
        Paid: {paid}
      </span>
      <span
        className="text-orange-600 dark:text-orange-400"
        data-testid="payments-total-outstanding"
      >
        Outstanding: {outstanding}
      </span>
    </div>
  );
}

function PaymentRowItem({
  invoice,
  payment,
  canDelete,
  onChanged,
  onDelete,
}: Readonly<{
  invoice: InvoiceRow;
  payment: PaymentRow;
  canDelete: boolean;
  onChanged: () => void;
  onDelete: (payment: PaymentRow) => void;
}>) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-sm">
      <dl className="flex flex-wrap gap-x-4 gap-y-1">
        {PAYMENT_FIELDS.map((field) => {
          const value = field.value(payment);
          if (value === null) return null;
          return (
            <div key={field.label} className="flex flex-col gap-0.5">
              <dt className="text-muted-foreground text-xs">{field.label}</dt>
              <dd className="font-medium">{value}</dd>
            </div>
          );
        })}
      </dl>
      <PaymentRowActions
        invoice={invoice}
        payment={payment}
        canDelete={canDelete}
        onChanged={onChanged}
        onDelete={onDelete}
      />
    </li>
  );
}

function PaymentRowActions({
  invoice,
  payment,
  canDelete,
  onChanged,
  onDelete,
}: Readonly<{
  invoice: InvoiceRow;
  payment: PaymentRow;
  canDelete: boolean;
  onChanged: () => void;
  onDelete: (payment: PaymentRow) => void;
}>) {
  const canEditReference = isRaiseMarker(payment) && invoice.invoice_direction === 'Purchase';
  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline">{payment.status}</Badge>
      {canEditReference ? (
        <EditReferenceControl invoice={invoice} payment={payment} onSaved={onChanged} />
      ) : null}
      {canDelete ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-11"
          aria-label="Delete payment"
          data-testid={`invoice-detail-${invoice.id}-payment-${payment.id}-delete`}
          onClick={() => {
            onDelete(payment);
          }}
        >
          <Trash2 className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

function EditReferenceControl({
  invoice,
  payment,
  onSaved,
}: Readonly<{ invoice: InvoiceRow; payment: PaymentRow; onSaved: () => void }>) {
  const [open, setOpen] = useState(false);
  const currentValue =
    payment.transfer_ref ?? payment.airwallex_transfer_id ?? payment.bank_reference;
  const hasReference = currentValue !== null && currentValue !== '';
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-11 gap-1"
        data-testid={`invoice-detail-${invoice.id}-payment-${payment.id}-edit-reference`}
        onClick={() => {
          setOpen(true);
        }}
      >
        <Pencil className="size-3.5" aria-hidden />
        {hasReference ? 'Edit reference' : 'Provide reference'}
      </Button>
      <EditTransferReferenceDialog
        open={open}
        onOpenChange={setOpen}
        invoice={invoice}
        currentValue={currentValue}
        onSaved={onSaved}
      />
    </>
  );
}
